/**
 * Schrank-Logik (Aura v2, Okt 2026), ohne React: Anziehen, Ablegen, Konflikte.
 *
 * Ein Outfit ist { slot: achievementKey } (wie PUT /api/v1/otter/outfit).
 * Ein Platz traegt ein Teil; `conflicts` aus dem Katalog nennt Teile anderer
 * Plaetze, die nicht zusammen passen (symmetrisch gepflegt). Wer ein Teil
 * anzieht, legt das kollidierende automatisch ab.
 *
 * Gemeinsam genutzt von OtterWardrobePage und UnlockSheet (ueber useUnlocks).
 */
import { resolveDisplayLevel } from '../constants/auraLevels';
import { SLOT_ORDER, partLabel } from '../constants/otterMoments';

/** Katalog-Liste -> { key: entry } */
export const indexCatalog = (catalog) => (Array.isArray(catalog) ? catalog : []).reduce((acc, entry) => {
  if (entry && entry.key) acc[entry.key] = entry;
  return acc;
}, {});

/** Nur gueltige Plaetze mit einem Schluessel */
export const cleanOutfit = (outfit) => SLOT_ORDER.reduce((acc, slot) => {
  const key = outfit && outfit[slot];
  if (typeof key === 'string' && key) acc[slot] = key;
  return acc;
}, {});

const conflictsWith = (a, b, byKey) => {
  const ea = byKey[a];
  const eb = byKey[b];
  return Boolean((ea?.conflicts || []).includes(b) || (eb?.conflicts || []).includes(a));
};

/**
 * Teil an- oder ablegen.
 *
 * @returns {{ outfit: Object, action: 'on'|'off'|'none', removed: string[] }}
 *   removed = Schluessel, die wegen eines Konflikts abgelegt wurden (nicht das
 *   Teil, das auf demselben Platz einfach getauscht wurde).
 */
export const toggleItem = (outfit, key, catalog) => {
  const byKey = Array.isArray(catalog) ? indexCatalog(catalog) : (catalog || {});
  const current = cleanOutfit(outfit);
  const entry = byKey[key];
  if (!entry || !entry.slot || !entry.accessory) return { outfit: current, action: 'none', removed: [] };

  if (current[entry.slot] === key) {
    const next = { ...current };
    delete next[entry.slot];
    return { outfit: next, action: 'off', removed: [] };
  }
  return wearItem(current, key, byKey);
};

/** Teil anziehen (nie ablegen); kollidierende Teile anderer Plaetze gehen runter */
export const wearItem = (outfit, key, catalog) => {
  const byKey = Array.isArray(catalog) ? indexCatalog(catalog) : (catalog || {});
  const current = cleanOutfit(outfit);
  const entry = byKey[key];
  if (!entry || !entry.slot || !entry.accessory) return { outfit: current, action: 'none', removed: [] };

  const next = { ...current, [entry.slot]: key };
  const removed = [];
  SLOT_ORDER.forEach((slot) => {
    const other = next[slot];
    if (slot === entry.slot || !other) return;
    if (conflictsWith(key, other, byKey)) {
      delete next[slot];
      removed.push(other);
    }
  });
  return { outfit: next, action: 'on', removed };
};

/** Accessory-Namen fuer die OtterFigure, in Platz-Reihenfolge */
export const outfitParts = (outfit, catalog) => {
  const byKey = Array.isArray(catalog) ? indexCatalog(catalog) : (catalog || {});
  const clean = cleanOutfit(outfit);
  return SLOT_ORDER
    .map((slot) => byKey[clean[slot]]?.accessory)
    .filter(Boolean);
};

/** Kurzer Hinweis nach einem Konflikt: "Cap abgelegt, passt nicht zur Taucherbrille." */
export const conflictNote = (key, removed, catalog) => {
  if (!removed || removed.length === 0) return '';
  const byKey = Array.isArray(catalog) ? indexCatalog(catalog) : (catalog || {});
  const names = removed.map((k) => partLabel(byKey[k] || { key: k }));
  const list = names.length > 1
    ? `${names.slice(0, -1).join(', ')} und ${names[names.length - 1]}`
    : names[0];
  return `${list} abgelegt, passt nicht zusammen mit ${partLabel(byKey[key] || { key })}.`;
};

/** Angezeigtes Level der eingeloggten Person (fuer die Welle hinter dem Otter) */
export const otterLevelOf = (user) => resolveDisplayLevel({
  peakLevel: user?.peakLevel ?? user?.auraStats?.peakLevel,
  level: user?.auraLevel ?? user?.auraStats?.level,
});
